<script lang="ts" setup>
import { ref } from 'vue';
import {
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BSpinner,
} from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { BUCKET_DATA_TYPES } from '@/constants';
import { formatDateStringToIso } from '@/utils/date-utils';
import BucketList from '@/components/data/BucketList.vue';
import { useUserStore } from '@/store/user';
import BucketCreate from '@/components/data/BucketCreate.vue';

const userStore = useUserStore();
const showNewBucketForm = ref(false);

// --- Component References ---
const bucketListRef = ref<InstanceType<typeof BucketList>>();

// Filter state
const bucketFriendlyNamePattern = ref('');

const dateCreatedAfter = ref<string | null>(null);
const timeCreatedAfter = ref<string | null>(null);
const dateTimeCreatedAfter = ref<string | null>(null);

const dateCreatedBefore = ref<string | null>(null);
const timeCreatedBefore = ref<string | null>(null);
const dateTimeCreatedBefore = ref<string | null>(null);

function onSearchRequested() {
  dateTimeCreatedAfter.value = formatDateStringToIso(
    dateCreatedAfter.value,
    true
  );
  dateTimeCreatedBefore.value = formatDateStringToIso(
    dateCreatedBefore.value,
    true
  );

  bucketListRef.value?.requestSearch();
}

function clearFilter() {
  bucketFriendlyNamePattern.value = '';

  dateCreatedBefore.value = null;
  timeCreatedBefore.value = null;
  dateTimeCreatedBefore.value = null;

  dateCreatedAfter.value = null;
  timeCreatedAfter.value = null;
  dateTimeCreatedAfter.value = null;

  bucketListRef.value?.requestSearch();
}

function onBucketCreated() {
  bucketListRef.value?.requestSearch();
  showNewBucketForm.value = false;
}
</script>

<template>
  <div>
    <h1><font-awesome-icon :icon="['fas', 'database']" /> Data Manager</h1>
    <h5>Browse your data and samples</h5>
    <hr />
    <p>
      Use this data manager to browse and edit your data, and view and annotate
      samples.
    </p>

    <!-- Create New Bucket option for admins and developers -->
    <div v-if="userStore.isAdmin || userStore.isDeveloper" class="my-3">
      <BButton variant="outline-dark" @click="showNewBucketForm = true">
        <font-awesome-icon :icon="['fa', 'plus']" class="me-1" />
        Create New Dataset
      </BButton>
      <BucketCreate
        v-if="showNewBucketForm"
        :show="showNewBucketForm"
        :default-data-type="BUCKET_DATA_TYPES.INPUT"
        @success="onBucketCreated"
        @close="showNewBucketForm = false"
      />
    </div>

    <!-- Filter -->
    <div class="my-3">
      <BForm
        class="d-flex flex-row align-items-end flex-wrap gap-2"
        @submit.prevent="onSearchRequested"
      >
        <!-- Bucket friendly name -->
        <BFormGroup label="Bucket name" label-for="bucket-name">
          <BFormInput
            id="search-text"
            type="text"
            v-model="bucketFriendlyNamePattern"
          />
        </BFormGroup>
        <!-- Date created after -->
        <BFormGroup label="Created after" label-for="date-created-after">
          <BFormInput
            id="date-created-after"
            type="date"
            v-model="dateCreatedAfter"
          />
        </BFormGroup>
        <!-- Date created before -->
        <BFormGroup label="Created before" label-for="date-created-before">
          <BFormInput
            id="date-created-before"
            type="date"
            v-model="dateCreatedBefore"
          />
        </BFormGroup>
        <!-- Filter action buttons -->
        <BButton variant="outline-secondary" @click="clearFilter">
          <font-awesome-icon icon="trash" class="me-1" />
          Clear filter
        </BButton>
        <BButton
          variant="outline-dark"
          type="submit"
          :disabled="bucketListRef?.bucketsQueryInProgress"
        >
          <span v-if="bucketListRef?.bucketsQueryInProgress">
            <b-spinner small label=""></b-spinner>
            Searching
          </span>
          <span v-else>
            <font-awesome-icon icon="magnifying-glass" class="me-1" />
            Search
          </span>
        </BButton>
      </BForm>
    </div>

    <!-- Bucket List -->
    <BucketList
      ref="bucketListRef"
      parentName="Data Manager"
      :data-type="BUCKET_DATA_TYPES.INPUT"
      :bucket-friendly-name-pattern="bucketFriendlyNamePattern"
      :created-after="dateTimeCreatedAfter"
      :created-before="dateTimeCreatedBefore"
    />
  </div>
  <!-- end of single div element-->
</template>
